import { useState, useCallback, useMemo } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import HeroSection from './components/HeroSection';
import RecommendedSection from './components/RecommendedSection';
import PopularSection from './components/PopularSection';
import CategoriesSection from './components/CategoriesSection';
import InquirySection from './components/InquirySection';
import InquiryModal from './components/InquiryModal';
import NoticeBanner from './components/NoticeBanner';
import BrandBannerSlider from './components/BrandBannerSlider';
import type { Product } from '@/mocks/products';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function Home() {
  const [modalProduct, setModalProduct] = useState<Product | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<{ cat: string; ts: number }>({ cat: '', ts: 0 });

  const homeJsonLd = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '한국 B2B 도매 카탈로그',
      url: siteUrl,
      description: '온라인 판매자·도매 바이어를 위한 한국 식품, 화장품, 생활용품 B2B 전문 공급 카탈로그',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}/search?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '한국 B2B 도매 카탈로그',
      url: siteUrl,
      description: '국내 우수 식품·화장품·생활용품을 엄선하여 도매 바이어와 온라인 판매자에게 공급하는 전문 B2B 카탈로그 플랫폼',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+82-2-300-1234',
        contactType: 'customer service',
        availableLanguage: 'Korean',
      },
      areaServed: 'KR',
    },
  ], []);

  const handleInquiry = useCallback((product: Product) => {
    setModalProduct(product);
  }, []);

  const handleHeroInquiry = useCallback(() => {
    const el = document.getElementById('inquiry');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleCategoryClick = useCallback((cat: string) => {
    setCategoryFilter({ cat, ts: Date.now() });
    const el = document.getElementById('categories');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
      <SeoHead
        title="한국 B2B 도매 카탈로그 | 식품·화장품·생활용품 공급"
        description="온라인 판매자·도매 바이어를 위한 한국 식품, 화장품, 생활용품 B2B 전문 공급 카탈로그. 2,000여 개 상품, 300개 브랜드, 최저가 도매 공급."
        keywords="한국 B2B 도매, 식품 도매, 화장품 도매, 생활용품 도매, 한국 상품 공급"
        canonical={`${siteUrl}/`}
        jsonLd={homeJsonLd}
      />
      <NoticeBanner />
      <Header onCategoryClick={handleCategoryClick} />

      <main>
        <HeroSection onInquiryClick={handleHeroInquiry} onCategoryClick={handleCategoryClick} />
        <BrandBannerSlider onCategoryClick={handleCategoryClick} />
        <RecommendedSection onInquiry={handleInquiry} />
        <PopularSection onInquiry={handleInquiry} />
        <CategoriesSection onInquiry={handleInquiry} defaultCategory={categoryFilter.cat} categoryKey={categoryFilter.ts} />
        <InquirySection />
      </main>

      <Footer />

      <InquiryModal
        product={modalProduct}
        onClose={() => setModalProduct(null)}
      />
    </div>
  );
}
